<template>
    <BasePage ref="basePage">
        <div class="container">
            <div v-if="loadError" class="card mt-6 py-6 px-3 text-center" role="alert">
                <h1 class="h2">We couldn't load these products</h1>
                <p>Please check your connection and try again.</p>
                <div><button type="button" class="btn btn-primary" @click="getProducts()">Try again</button></div>
            </div>
            <div v-else-if="loaded && !group" class="card mt-6 py-6 px-3 text-center">
                <h1 class="h2">We couldn't find that category</h1>
                <p class="mb-0"><router-link to="/">Back to the shop</router-link></p>
            </div>
            <template v-else-if="loaded">
                <div class="row align-items-end g-3 mt-3 mt-md-5">
                    <div class="col-12 col-md">
                        <h1 class="h2 mb-0" data-testid="group-heading">
                            <template v-if="type">
                                <router-link class="text-reset" :to="'/products/' + group.groupSlug">{{ group.groupName }}</router-link>
                                <span aria-hidden="true"> › </span>{{ type.typeName }}
                            </template>
                            <template v-else>{{ group.groupName }}</template>
                            <span class="text-secondary fw-normal"> ({{ products.length }})</span>
                        </h1>
                    </div>
                    <div class="col-12 col-md-4">
                        <label class="form-label visually-hidden" for="product-filter">Search these products</label>
                        <input id="product-filter" type="search" class="form-control" v-model="filterText"
                               :placeholder="'Search ' + (type ? type.typeName : group.groupName)" autocomplete="off">
                    </div>
                </div>
                <p v-if="products.length === 0" class="mt-4" role="status">
                    <template v-if="filterText.trim()">Nothing here matches "{{ filterText.trim() }}".
                        <button type="button" class="btn btn-link p-0 align-baseline" @click="filterText = ''">Clear the search</button>
                    </template>
                    <template v-else>There are no products here at the moment.</template>
                </p>
                <ProductTiles v-else :products="products"></ProductTiles>
            </template>
        </div>
    </BasePage>
</template>

<script>
import ProductTiles from "@/components/ProductTiles.vue";
import BasePage from '@/components/BasePage.vue';
import api from '@/services/api'

import { setPageTitle } from '@/services/pageTitle'
import { indexProducts, searchProducts } from '@/services/productSearch'

export default {
    name: 'GroupPage',
    components: {
        ProductTiles,
        BasePage,
    },
    data() {
        return {
            groups: [],
            types: [],
            everyProduct: [],
            filterText: '',
            loaded: false,
            loadError: false
        }
    },
    created() {
        this.getProducts();
    },
    computed: {
        group() {
            return this.groups.find(g => g.groupSlug === this.$route.params.groupId) || null;
        },
        type() {
            const slug = this.$route.query.type;
            return slug ? (this.types.find(t => t.typeSlug === slug) || null) : null;
        },
        pageHeading() {
            if (!this.group) return '';
            return this.type ? this.type.typeName + ' - ' + this.group.groupName : this.group.groupName;
        },
        inCategory() {
            if (!this.group) return [];
            return this.everyProduct.filter(p => {
                if (p.group !== this.group.groupId) return false;
                // An unknown ?type= shows the whole category rather than nothing.
                return !(this.type && p.type !== this.type.typeId);
            });
        },
        // The same forgiving search as the home page (services/productSearch.js), within this
        // category: matches by name, cut or code first, then products whose description mentions it.
        products() {
            if (!this.filterText.trim()) return this.inCategory;
            const typeNames = new Map(this.types.map(t => [t.typeId, t.typeName]));
            const index = indexProducts(this.inCategory, p => [typeNames.get(p.type)].filter(Boolean));
            const found = searchProducts(index, this.filterText);
            return [...found.matches, ...found.alsoMentioned];
        }
    },
    methods: {
        async getProducts() {
            this.loadError = false;
            try {
                const [groupsRes, typesRes, productsRes] = await Promise.all([
                    api.get('/api/groups'),
                    api.get('/api/types'),
                    api.get('/api/products'),
                ]);
                if (!Array.isArray(groupsRes.data) || !Array.isArray(typesRes.data) || !Array.isArray(productsRes.data)) {
                    throw new Error('Unexpected reply');
                }
                this.groups = groupsRes.data;
                this.types = typesRes.data;
                this.everyProduct = productsRes.data;
                this.loaded = true;
            } catch (e) {
                this.loadError = true;
            }
        }
    },
    watch: {
        // Moving between categories or types starts with an empty search box.
        '$route.fullPath': function () {
            this.filterText = '';
        },
        // FE-118: the category (and cut) name as the page title, once known.
        pageHeading: {
            handler(heading) {
                if (heading) setPageTitle(heading);
            },
            immediate: true,
            flush: 'post'
        }
    }
};
</script>
